/**
 * Browser-side auth glue. Cognito SRP via amazon-cognito-identity-js
 * with a deliberately split storage strategy:
 *
 * - **Access + id tokens → localStorage.** Operator dogfood 2026-05-04:
 *   opening a transaction in a new tab forced re-login because the
 *   prior sessionStorage scope was per-tab. POS-row-click → new tab is
 *   the dominant interaction pattern, so cross-tab session inheritance
 *   wins.
 * - **Refresh token → memory only.** Nothing in the SPA consumes it; a
 *   long-lived Cognito refresh token left in localStorage on a shared
 *   POS terminal after a forgotten sign-out would be a real exposure.
 * - **Active membership id → sessionStorage.** Per-tab. Sharing the
 *   active scope across tabs lets tab A render data for org X while
 *   the next API call from tab A goes out with org Y's header (a
 *   wrong-scope data/action bug).
 *
 * Acceptable POS tradeoffs: localStorage access + id tokens persist
 * across browser restarts until the user signs out (or the JWT exp
 * fires - see ``readAccessToken``). /profile on every page mount
 * validates the session; on 401 the UI redirects to /login.
 *
 * The POS pool is configured with MFA ON. Users without a TOTP
 * device enrolled see the MFA_SETUP challenge on first sign-in.
 * The ``mfaSetup`` callback triggers the enrollment QR flow in
 * LoginPage; thereafter TOTP is required every sign-in.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type ICognitoUserPoolData,
} from "amazon-cognito-identity-js";

import type { TokenStore } from "@tilt/pos-sdk/auth";

const ACCESS_KEY = "tilt-pos.access_token";
const ID_KEY = "tilt-pos.id_token";
const REFRESH_KEY = "tilt-pos.refresh_token";
const ACTIVE_KEY = "tilt-pos.active_membership_id";

export interface CognitoConfig {
  readonly userPoolId: string;
  readonly clientId: string;
}

export function makeUserPool({ userPoolId, clientId }: CognitoConfig): CognitoUserPool {
  const data: ICognitoUserPoolData = {
    UserPoolId: userPoolId,
    ClientId: clientId,
  };
  return new CognitoUserPool(data);
}

export interface SignInResult {
  readonly accessToken: string;
  readonly idToken: string;
  readonly refreshToken: string;
}

export type PendingMfa =
  | { readonly kind: "TOTP_REQUIRED"; readonly user: CognitoUser }
  | { readonly kind: "NEW_PASSWORD_REQUIRED"; readonly user: CognitoUser }
  | {
      readonly kind: "MFA_SETUP";
      readonly user: CognitoUser;
      readonly secretCode: string;
      readonly qrUri: string;
    };

export type SignInOutcome = SignInResult | PendingMfa;

/**
 * Run the SRP flow against the POS Cognito pool. Returns either a
 * tokens bundle (success) or a ``PendingMfa`` envelope the caller
 * uses to drive the second-factor prompt.
 */
export function signIn(
  pool: CognitoUserPool,
  email: string,
  password: string,
): Promise<SignInOutcome> {
  const user = new CognitoUser({ Username: email, Pool: pool });
  const auth = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve(stashSession(session)),
      onFailure: (err) => reject(err),
      totpRequired: () => resolve({ kind: "TOTP_REQUIRED", user }),
      newPasswordRequired: () => resolve({ kind: "NEW_PASSWORD_REQUIRED", user }),
      mfaSetup: () =>
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) =>
            resolve({
              kind: "MFA_SETUP",
              user,
              secretCode,
              qrUri: otpAuthUri(email, secretCode),
            }),
          onFailure: (err) => reject(err),
        }),
    });
  });
}

export function completeTotp(user: CognitoUser, code: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(stashSession(session)),
        onFailure: (err) => reject(err),
      },
      "SOFTWARE_TOKEN_MFA",
    );
  });
}

export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  email: string,
): Promise<SignInResult | PendingMfa> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => resolve(stashSession(session)),
        onFailure: (err) => reject(err),
        mfaSetup: () =>
          user.associateSoftwareToken({
            associateSecretCode: (secretCode) =>
              resolve({
                kind: "MFA_SETUP",
                user,
                secretCode,
                qrUri: otpAuthUri(email, secretCode),
              }),
            onFailure: (err) => reject(err),
          }),
      },
    );
  });
}

/** Confirm TOTP enrollment after scanning the QR code. */
export function verifyTotpSetup(user: CognitoUser, code: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    user.verifySoftwareToken(code, "Tilt POS", {
      onSuccess: () => {
        user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) => {
          if (err) {
            reject(err);
            return;
          }
          user.getSession((sessionErr: Error | null, session: CognitoUserSession | null) => {
            if (sessionErr || !session) {
              reject(sessionErr ?? new Error("session unavailable"));
              return;
            }
            resolve(stashSession(session));
          });
        });
      },
      onFailure: (err) => reject(err),
    });
  });
}

function otpAuthUri(account: string, secret: string): string {
  const label = encodeURIComponent(`Tilt POS:${account}`);
  const issuer = encodeURIComponent("Tilt POS");
  return `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
}

/** Build a CognitoUser handle for the resolved Username (UUID in the
 *  POS pool - see /pos/v1/public/sign-in-init). The handle is what
 *  amazon-cognito-identity-js operates on for forgot-password,
 *  confirm-password, and resend-code. */
export function makeUser(pool: CognitoUserPool, username: string): CognitoUser {
  return new CognitoUser({ Username: username, Pool: pool });
}

/** Trigger Cognito to email the user a 6-digit reset code.
 *  Resolves on success so the caller can transition to the
 *  confirm-code stage. */
export function forgotPassword(user: CognitoUser): Promise<void> {
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      // ``onSuccess`` fires on the SECOND step (after confirmPassword).
      // For the email-send-only step Cognito's library calls
      // ``inputVerificationCode`` instead, which we treat as success
      // for this stage so the UI advances.
      inputVerificationCode: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Finalise a forgot-password reset with the emailed code +
 *  the new password the user typed. Resolves once Cognito
 *  has accepted the new credential; caller should auto sign-in
 *  with the same email/password to land a session. */
export function confirmForgotPassword(
  user: CognitoUser,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

function stashSession(session: CognitoUserSession): SignInResult {
  const access = session.getAccessToken().getJwtToken();
  const id = session.getIdToken().getJwtToken();
  const refresh = session.getRefreshToken().getToken();
  // Access + id tokens go in localStorage so a new tab inherits the
  // session (POS row-click → new-tab is the dominant interaction).
  // Refresh token is intentionally NOT persisted: nothing in the SPA
  // consumes it, and a 30-day Cognito refresh token sitting on a shared
  // POS terminal after a forgotten sign-out is a real exposure. The
  // shape of SignInResult is preserved for callers, but we keep the
  // value in memory only.
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(ID_KEY, id);
  return { accessToken: access, idToken: id, refreshToken: refresh };
}

export function clearSession(): void {
  console.debug("[auth] clearSession called");
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(ID_KEY);
  // Defensive: prior builds did persist the refresh token. Clear any
  // stale value so an upgraded client doesn't carry it forward.
  localStorage.removeItem(REFRESH_KEY);
  // ACTIVE_KEY is sessionStorage-scoped (per tab) - see
  // ``readActiveMembershipId``. Clear it on the same surface.
  sessionStorage.removeItem(ACTIVE_KEY);
  // Clear inactivity-lock flag so a sign-out + sign-in on the same tab
  // doesn't immediately re-show the lock screen.
  sessionStorage.removeItem("tilt-pos.locked");
}

/** Read the access token IF it exists AND hasn't passed JWT ``exp``.
 *  Returns null otherwise. Without this check, an expired-but-present
 *  token traps the SPA in a redirect loop:
 *    - ``RequireAuth`` sees a non-empty token → renders the route
 *    - The route's API call gets 401 → bounces to /login
 *    - ``LoginPage``'s mount gate sees the same non-empty token →
 *      ``<Navigate to={from} />`` back to the protected route
 *    - Loop forever
 *  Sniffing ``exp`` here means an expired session reads as "logged
 *  out" and the user gets a clean re-auth prompt instead of flicker.
 *  We don't verify the signature - Cognito does that server-side; the
 *  client only cares about freshness for routing decisions. */
export function readAccessToken(): string | null {
  const raw = localStorage.getItem(ACCESS_KEY);
  if (!raw) {
    console.debug("[auth] readAccessToken — no token in localStorage");
    return null;
  }
  if (isJwtExpired(raw)) {
    // Defensive cleanup so the next read is fast + the LoginPage's
    // own gate sees an empty store. Refresh-token recovery happens at
    // a higher layer (PosClient onUnauthorized → fresh sign-in); we
    // don't transparently refresh here because that requires the
    // refresh token + Cognito SDK + can fail itself.
    try {
      const parts = raw.split(".");
      const payload = parts[1]
        ? (JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/") + "==")) as {
            exp?: number;
            iat?: number;
          })
        : {};
      console.debug("[auth] readAccessToken — token expired, clearing session", {
        exp: payload.exp,
        iat: payload.iat,
        now: Math.floor(Date.now() / 1000),
      });
    } catch {
      console.debug("[auth] readAccessToken — token unparseable, clearing session");
    }
    clearSession();
    return null;
  }
  return raw;
}

export function readIdToken(): string | null {
  return localStorage.getItem(ID_KEY);
}

/** Decode a JWT's payload (no signature check) and check ``exp``.
 *  Returns true when expired, can't be parsed, or has no exp claim.
 *  Conservative: an unparseable token is treated as expired so we
 *  never accidentally let a malformed value through the auth gate. */
function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return true;
    const payload = parts[1]!;
    // Base64-url → base64. Pad to multiple of 4 because some encoders
    // strip the trailing ``=``.
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof decoded.exp !== "number") return true;
    // 30s skew so a token about to expire isn't used for an in-flight
    // request that lands milliseconds after the boundary.
    const nowSeconds = Math.floor(Date.now() / 1000);
    return decoded.exp - 30 <= nowSeconds;
  } catch {
    return true;
  }
}

/**
 * Active membership lives in sessionStorage (per-tab), NOT
 * localStorage. Two reasons:
 * 1. ``PosClient`` reads ``getActiveMembershipId()`` on every request,
 *    but per-page React Query caches don't invalidate when another
 *    tab flips the scope. Sharing the active scope across tabs lets
 *    tab A render data for org X while its next API call goes out
 *    with org Y's ``X-Active-Membership-Id`` header - a wrong-scope
 *    data/action bug.
 * 2. The auth tokens themselves DO live in localStorage so a new tab
 *    inherits the session. New tabs inherit the session but pick
 *    their own active scope (defaulting to the user's first
 *    membership from /profile when sessionStorage is empty).
 */
export function readActiveMembershipId(): string | null {
  return sessionStorage.getItem(ACTIVE_KEY);
}

export function writeActiveMembershipId(id: string | null): void {
  if (id) sessionStorage.setItem(ACTIVE_KEY, id);
  else sessionStorage.removeItem(ACTIVE_KEY);
}

/**
 * TokenStore implementation that pulls from localStorage. Passed
 * to ``new PosClient`` and the host app exposes ``onUnauthorized`` so
 * a 401 redirects to /login.
 */
export function makeTokenStore(onUnauthorized: () => void): TokenStore {
  return {
    getAccessToken: () => readAccessToken(),
    getIdToken: () => readIdToken(),
    onUnauthorized,
  };
}
